import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Button,
  Card,
  Grid,
  InputAdornment,
  SvgIcon,
  TextField
} from '@material-ui/core';
import { BranchListDropDown } from 'src/components/dropdown';
import { CategoryListDropdown } from 'src/components/filters/CategoryDropdown';
import { Search as SearchIcon } from 'react-feather';
import FindInPageIcon from '@material-ui/icons/FindInPage';
import { SelectProductAuditModal } from './SelectProductAuditModal';
import { useIsMount, useSnackBar } from 'src/hooks';
import { useInventoryAudit } from 'src/hooks/inventory-audit';
import { CustomKeyboardEvent } from 'src/types';
import ReplayIcon from '@material-ui/icons/Replay';
import { DecisionDialog } from 'src/components';
import usePrompt from 'src/utils/navigation-prompt';
import useUserInfo from 'src/hooks/user/use-user-info';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { isEmpty } from 'lodash';
import SimpleDecisionDialog from 'src/components/dialogs/SimpleDecisionDialog';

interface Props {}

const {
  actions: categoryActions,
  selectors: categorySelectors
} = slices.category;

const component: FC<Props> = () => {
  const snackBar = useSnackBar();
  const isFirstMount = useIsMount();
  const dispatch = useAppDispatch();
  const { userBranchOptions } = useUserInfo();
  const categories = useAppSelector(categorySelectors.selectCategories);

  const {
    hasAuditListing,
    commonAuditParams,
    handleCommonAuditParams,
    getProductListingViaSerialNo,
    resetInventoryAudit
  } = useInventoryAudit();
  const [scanSn, setScanSn] = useState<string>('');
  // const [paramsFilter, setParamsFilter] = useState<CommonAuditParams>({});
  const [productAuditOpen, setProductAuditOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isConfirmResumeOpen, setIsConfirmResumeOpen] = useState<boolean>(
    false
  );

  const branchName = useMemo(() => {
    return commonAuditParams?.branch_name;
  }, [commonAuditParams]);

  const categoryName = useMemo(() => {
    return commonAuditParams?.category_name;
  }, [commonAuditParams]);

  const onHandleScanSn = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScanSn(event?.target?.value);
  };

  const onHandleClickSelectProductAuditModal = useCallback(() => {
    if (!commonAuditParams?.branch_id || !commonAuditParams?.category_id) {
      snackBar.show({
        severity: 'error',
        message: 'Both branch and category is required'
      });
      return;
    }
    setProductAuditOpen(!productAuditOpen);
  }, [commonAuditParams, productAuditOpen, snackBar]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!scanSn) return;
    getProductListingViaSerialNo(scanSn);
    setScanSn('');
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      getProductListingViaSerialNo(scanSn);
      setScanSn('');
    }
  };

  const onHandleResetInventoryAudit = () => {
    setIsOpen(true);
  };

  const getCategories = useCallback(async () => {
    try {
      await dispatch(
        categoryActions.getCategoriesThunk({
          ...(commonAuditParams?.category_id && {
            id: commonAuditParams?.category_id
          })
        })
      );
    } catch (error) {
      console.error(error);
    }
  }, [commonAuditParams, dispatch]);

  useEffect(() => {
    if (scanSn) {
      handleCommonAuditParams({ ...commonAuditParams, keyword: scanSn });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonAuditParams]);

  useEffect(() => {
    if (hasAuditListing && isFirstMount) {
      // mark open dialog here
      setIsConfirmResumeOpen(true); // isfirstmount
    }
  }, [hasAuditListing, isFirstMount]);

  useEffect(() => {
    if (isEmpty(categories)) {
      getCategories();
    }
  }, [categories, getCategories]);

  usePrompt('You are about to leave without submitting', hasAuditListing);

  return (
    <Card style={{ padding: '1rem' }}>
      <Grid
        container
        spacing={1}
        style={{
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}
      >
        {hasAuditListing ? (
          <>
            <Grid item md={5} xs={9}>
              <form onSubmit={handleSubmit}>
                <TextField
                  onChange={onHandleScanSn}
                  value={scanSn}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon fontSize="small" color="action">
                          <SearchIcon />
                        </SvgIcon>
                      </InputAdornment>
                    )
                  }}
                  placeholder="Scan Or Type Product SN"
                  variant="outlined"
                  onKeyDown={onKeyPress} // still works for desktop keyboards
                />
                {/* ensures mobile soft keyboards trigger submit */}
                <button type="submit" style={{ display: 'none' }} />
              </form>
            </Grid>
            <Grid item md={1} xs={3}>
              <Button
                fullWidth
                variant="outlined"
                style={{ height: '55px' }}
                onClick={() => {
                  getProductListingViaSerialNo(scanSn);
                  setScanSn('');
                }}
              >
                <SearchIcon />
              </Button>
            </Grid>
          </>
        ) : (
          <>
            <Grid item md={3} sm={6} xs={12}>
              <BranchListDropDown
                isElevated={false}
                allowNoSelectedBranch={false}
                paperSpace={0}
                onHandleBranchChange={(branch?: number[]) => {
                  const branchId = branch?.[0] ? branch?.[0] : undefined; // Extract the single item
                  const branchName =
                    userBranchOptions?.find((b) => b.value === branchId)
                      ?.name ?? undefined;

                  handleCommonAuditParams({
                    ...commonAuditParams,
                    branch_id: branchId,
                    branch_name: branchName
                  });
                }}
              />
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
              <CategoryListDropdown
                onHandleCategories={(categoryId: number) => {
                  handleCommonAuditParams({
                    ...commonAuditParams,
                    category_id: categoryId,
                    category_name:
                      categories.find((c) => c.id === categoryId)?.name ??
                      undefined
                  });
                }}
              />
            </Grid>
          </>
        )}

        <Grid item md={3} xs={12}>
          <Button
            style={{ marginRight: '1rem', height: '55px' }}
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={onHandleClickSelectProductAuditModal}
            startIcon={<FindInPageIcon />}
          >
            Select Product/s
          </Button>
        </Grid>

        {hasAuditListing && (
          <Grid item md={3} xs={12}>
            <Button
              style={{ marginRight: '1rem', height: '55px' }}
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={onHandleResetInventoryAudit}
              startIcon={<ReplayIcon />}
            >
              Reset Inventory Audit
            </Button>
          </Grid>
        )}
      </Grid>
      <SelectProductAuditModal
        isOpen={productAuditOpen}
        onHandleClose={() => setProductAuditOpen(false)}
      />

      <DecisionDialog
        isOpen={isOpen}
        title="Confirm Reset?"
        subTitle="This will permanently clear all audit counts and serials."
        onHandleConfirmAction={resetInventoryAudit}
        onHandleClose={() => setIsOpen(false)}
      />

      <SimpleDecisionDialog
        isOpen={isConfirmResumeOpen}
        title="Resume audit progress?"
        subTitle={`We found an unfinished audit for Branch ${branchName?.toUpperCase()}, Category ${categoryName}.
             Do you want to continue where you left off or start a new one?`}
        confirmButtonLabel="Continue"
        cancelButtonLabel="Start new"
        disableBackDropClick={true}
        onHandleConfirmAction={() => {
          setIsConfirmResumeOpen(false);
        }}
        onHandleClose={() => {
          setIsConfirmResumeOpen(false);
          resetInventoryAudit();
        }}
      />
    </Card>
  );
};

export const Toolbar = memo(component);
